import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import {
  ContentPagesResponseDto,
  UpdateContentPagesRequestDto,
} from '@tiendanube/common';
import HelpLink from 'App/HelpLink';
import { useNavegate } from 'App/hooks';
import { LanguageNameValueInterface } from 'App/i18n';
import { useForm } from 'commons/hooks';
import { StatusType } from 'commons/types';
import { useGetAllLanguages } from 'domains/Auth/hooks';
import { validationSchema } from 'domains/Online/ContentPages/validationSchema';
import useTranslationOnline from 'domains/Online/useTranslationOnline';
import { DesignEditorLaunchModal, FormPage } from '../../components';
import FormFooter from '../../components/FormPage/components/FormFooter';
import { useDesignEditorLaunchModal } from '../../hooks';

interface EditFormPageProps {
  initialValues: ContentPagesResponseDto;
  updatePage: (payload: UpdateContentPagesRequestDto) => void;
  statusUpdate: StatusType;
}

function EditFormPage({
  initialValues,
  updatePage,
  statusUpdate,
}: EditFormPageProps) {
  const [isSaveForm, setIsSaveForm] = useState(false);
  const t = useTranslationOnline('contentPages');
  const { t: tCommon } = useTranslation();
  const [language] = useGetAllLanguages();
  const { goBack } = useNavegate();
  const { values, errors, isDirty, setFieldValue, handleOnSubmit } = useForm({
    initialValues,
    validationSchema: validationSchema(language),
    onSubmit: updatePage,
  });

  const {
    showDesignModal,
    themesConfig,
    handleOpenEditDesignModal,
    handleCloseEditDesignModal,
    handleDesignEditModalSubmit,
  } = useDesignEditorLaunchModal({
    pageId: initialValues.id,
  });

  const showPrompt = isDirty && !isSaveForm;

  const handleSubmit = useCallback(() => {
    setIsSaveForm(true);
    handleOnSubmit();
  }, [handleOnSubmit]);

  const handleChange = useCallback(
    ({ name, value }: LanguageNameValueInterface) => {
      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  const handleShowInStoreChange = useCallback(
    (value: boolean) => {
      setFieldValue('showInStore', value);
    },
    [setFieldValue],
  );

  return (
    <>
      <Prompt when={showPrompt} message={tCommon('common:exitEdit.info')} />
      <FormPage
        values={values}
        errors={errors}
        themesConfig={themesConfig}
        onChange={handleChange}
        onDesignEditClick={handleOpenEditDesignModal}
      />
      <FormFooter
        values={values}
        onShowInStoreChange={handleShowInStoreChange}
        onCancel={goBack}
        onSave={handleSubmit}
        saveText={t('formPage.saveButton')}
        isDisabled={!isDirty}
        isLoading={statusUpdate === 'loading'}
      />
      <HelpLink
        title={t('helpLink.title')}
        linkURL={t('helpLink.linkURL')}
        previousValue=""
        currentViewTracking={t('helpLink.currentViewTracking')}
        icon="both"
      />

      {showDesignModal && !!themesConfig && (
        <DesignEditorLaunchModal
          themesConfig={themesConfig}
          language={language}
          initialValues={values}
          isSubmitting={statusUpdate === 'loading'}
          onSubmit={handleDesignEditModalSubmit}
          onClose={handleCloseEditDesignModal}
        />
      )}
    </>
  );
}

export default EditFormPage;
